/**
 * Parse Microsoft Project / MPXJ dependency rows from schedule snapshot tasks.
 * Supports FS, SS, FF, SF link types and optional lag.
 */

export type EpcTaskRef = Record<string, unknown>;

export type ParsedPredecessorLink = {
  linkTypeCode: string;
  lag?: string;
  predecessorName?: string;
  predecessorWbs?: string;
  /**
   * MPXJ internal unique id when the exporter uses it correctly (`task.uniqueId`).
   * Some JSON exports mislabel MS Project Task ID as `predecessorUniqueId`; in that case it matches `task.id`, not `uniqueId`.
   */
  predecessorUniqueId?: unknown;
  /** MS Project task id — this is what appears in strings like `19SS+7 days` and matches `task.id`. */
  predecessorTaskId?: unknown;
  mpxjType?: string;
};

export type ParsedSuccessorLink = {
  linkTypeCode: string;
  lag?: string;
  successorName?: string;
  successorWbs?: string;
  successorUniqueId?: unknown;
  successorTaskId?: unknown;
  mpxjType?: string;
};

export const LINK_TYPE_LEGEND: {
  code: string;
  label: string;
  short: string;
  hint: string;
  example: string;
  frequency: 'most-common' | 'common' | 'occasional' | 'rare';
}[] = [
  {
    code: 'FS',
    label: 'Finish → Start',
    short: 'After',
    hint: 'This task starts only after the predecessor finishes. Default for sequential work.',
    example: 'Pour foundation → Build walls',
    frequency: 'most-common',
  },
  {
    code: 'SS',
    label: 'Start → Start',
    short: 'Together',
    hint: 'Both tasks begin at the same time — predecessor start drives this task start.',
    example: 'Excavation begins → Site survey begins',
    frequency: 'common',
  },
  {
    code: 'FF',
    label: 'Finish → Finish',
    short: 'End-aligned',
    hint: 'Both tasks must finish at the same time — predecessor finish drives this task finish.',
    example: 'Cable pulling → Cable testing wraps up',
    frequency: 'occasional',
  },
  {
    code: 'SF',
    label: 'Start → Finish',
    short: 'Just-in-time',
    hint: 'This task finishes when the predecessor starts. Used for handovers and shift work.',
    example: 'Night shift starts → Day shift ends',
    frequency: 'rare',
  },
];

export function normalizeLinkTypeCode(linkTypeCode: unknown, mpxjType: unknown): string {
  if (typeof linkTypeCode === 'string' && /^[A-Z]{2}$/i.test(linkTypeCode.trim())) {
    return linkTypeCode.trim().toUpperCase();
  }
  const t = String(mpxjType ?? '').toUpperCase();
  if (t.includes('FINISH_START') || t === 'FS') return 'FS';
  if (t.includes('START_START') || t === 'SS') return 'SS';
  if (t.includes('FINISH_FINISH') || t === 'FF') return 'FF';
  if (t.includes('START_FINISH') || t === 'SF') return 'SF';
  return '?';
}

export function linkPillClass(code: string): string {
  switch (code) {
    case 'FS':
      return 'bg-slate-100 text-slate-800 ring-1 ring-slate-200/90';
    case 'SS':
      return 'bg-sky-100 text-sky-900 ring-1 ring-sky-200/90';
    case 'FF':
      return 'bg-violet-100 text-violet-900 ring-1 ring-violet-200/90';
    case 'SF':
      return 'bg-amber-100 text-amber-950 ring-1 ring-amber-200/90';
    default:
      return 'bg-slate-50 text-slate-600 ring-1 ring-slate-200/80';
  }
}

function lagLabel(lag: string | undefined): string {
  if (lag == null || lag === '') return '';
  const trimmed = lag.trim();
  if (trimmed === '0.0d' || trimmed === '0d' || trimmed === '0') return '';
  return trimmed;
}

export function parsePredecessorLinks(raw: unknown): ParsedPredecessorLink[] {
  if (!Array.isArray(raw)) return [];
  const out: ParsedPredecessorLink[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const o = item as EpcTaskRef;
    const code = normalizeLinkTypeCode(o.linkTypeCode, o.type);
    const lag = o.lag != null ? String(o.lag) : undefined;
    const name = String(o.predecessorName ?? '').trim();
    const wbs = o.predecessorWbs != null ? String(o.predecessorWbs) : undefined;
    out.push({
      linkTypeCode: code,
      lag: lagLabel(lag),
      predecessorName: name || undefined,
      predecessorWbs: wbs,
      predecessorUniqueId: o.predecessorUniqueId,
      predecessorTaskId: o.predecessorTaskId ?? o.predecessorId,
      mpxjType: o.type != null ? String(o.type) : undefined,
    });
  }
  return out;
}

export function parseSuccessorLinks(raw: unknown): ParsedSuccessorLink[] {
  if (!Array.isArray(raw)) return [];
  const out: ParsedSuccessorLink[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const o = item as EpcTaskRef;
    const code = normalizeLinkTypeCode(o.linkTypeCode, o.type);
    const lag = o.lag != null ? String(o.lag) : undefined;
    const name = String(o.successorName ?? '').trim();
    const wbs = o.successorWbs != null ? String(o.successorWbs) : undefined;
    out.push({
      linkTypeCode: code,
      lag: lagLabel(lag),
      successorName: name || undefined,
      successorWbs: wbs,
      successorUniqueId: o.successorUniqueId,
      successorTaskId: o.successorTaskId ?? o.successorId,
      mpxjType: o.type != null ? String(o.type) : undefined,
    });
  }
  return out;
}

export function resolveNameByUniqueId(
  uid: unknown,
  tasks: EpcTaskRef[],
  /** When resolving labels for a row, skip this index so we never show the current task as the driver/follower name. */
  excludeRowIndex?: number
): string | undefined {
  if (uid == null) return undefined;
  const n = Number(uid);
  const idx = tasks.findIndex((t, i) => {
    if (excludeRowIndex != null && i === excludeRowIndex) return false;
    const u = t.uniqueId;
    if (u === uid) return true;
    if (typeof u === 'number' && Number.isFinite(n) && u === n) return true;
    if (typeof u === 'string' && String(uid) === u) return true;
    return false;
  });
  if (idx < 0) return undefined;
  const nm = tasks[idx]?.name;
  return typeof nm === 'string' && nm.trim() ? nm.trim() : undefined;
}

/** Row index of the task with this MPXJ / MPP unique id, if present. */
export function findTaskIndexByUniqueId(tasks: EpcTaskRef[], uid: unknown): number | null {
  if (uid == null || uid === '') return null;
  const n = Number(uid);
  const idx = tasks.findIndex((t) => {
    const u = t.uniqueId;
    if (u === uid) return true;
    if (typeof u === 'number' && Number.isFinite(n) && u === n) return true;
    if (typeof u === 'string' && String(uid) === u) return true;
    return false;
  });
  return idx >= 0 ? idx : null;
}

/**
 * Row index by MS Project “task id” (`Task.getID()` / JSON `id`).
 * Predecessor text like `19SS+7 days` refers to this id, not `uniqueId`.
 */
export function findTaskIndexByMspTaskId(tasks: EpcTaskRef[], mspTaskId: unknown): number | null {
  if (mspTaskId == null || mspTaskId === '') return null;
  const n = Number(mspTaskId);
  const idx = tasks.findIndex((t) => {
    const tid = t.id;
    if (tid === mspTaskId) return true;
    if (typeof tid === 'number' && Number.isFinite(n) && tid === n) return true;
    if (typeof tid === 'string' && String(tid) === String(mspTaskId)) return true;
    return false;
  });
  return idx >= 0 ? idx : null;
}

export function resolveNameByMspTaskId(
  mspTaskId: unknown,
  tasks: EpcTaskRef[],
  excludeRowIndex?: number
): string | undefined {
  const idx = findTaskIndexByMspTaskId(tasks, mspTaskId);
  if (idx == null) return undefined;
  if (excludeRowIndex != null && idx === excludeRowIndex) return undefined;
  const nm = tasks[idx]?.name;
  return typeof nm === 'string' && nm.trim() ? nm.trim() : undefined;
}

export function findTaskIndexByWbs(tasks: EpcTaskRef[], wbs: string | undefined): number | null {
  if (!wbs || !String(wbs).trim()) return null;
  const w = String(wbs).trim();
  const idx = tasks.findIndex((t) => {
    const tw = t.wbs;
    return typeof tw === 'string' && tw.trim() === w;
  });
  return idx >= 0 ? idx : null;
}

export function findTaskIndexByExactName(tasks: EpcTaskRef[], name: string | undefined): number | null {
  if (!name || !String(name).trim()) return null;
  const n = String(name).trim().toLowerCase();
  const idx = tasks.findIndex((t) => {
    const tn = typeof t.name === 'string' ? t.name.trim().toLowerCase() : '';
    return tn === n;
  });
  return idx >= 0 ? idx : null;
}

/** First name match skipping a row (avoids bogus “self” when the current row is the only name hit). */
export function findTaskIndexByExactNameExcluding(
  tasks: EpcTaskRef[],
  name: string | undefined,
  excludeRowIndex: number
): number | null {
  if (!name || !String(name).trim()) return null;
  const n = String(name).trim().toLowerCase();
  const idx = tasks.findIndex((t, i) => {
    if (i === excludeRowIndex) return false;
    const tn = typeof t.name === 'string' ? t.name.trim().toLowerCase() : '';
    return tn === n;
  });
  return idx >= 0 ? idx : null;
}

/**
 * Best-effort row index for a predecessor link.
 * Prefer MS Project Task ID (`id` / predecessorTaskId) over MPXJ `uniqueId`: UI strings like `4SS` refer to Task ID 4, and the two ids can differ.
 * @param forRowIndex When set, a resolved index equal to this row is never returned (no “jump to self”); bad .mpp/converter rows often pointed at the current task.
 */
export function resolvePredecessorTaskIndex(
  link: ParsedPredecessorLink,
  tasks: EpcTaskRef[],
  forRowIndex?: number
): number | null {
  const stepMspRaw = findTaskIndexByMspTaskId(tasks, link.predecessorTaskId);
  const stepUidRaw = findTaskIndexByUniqueId(tasks, link.predecessorUniqueId);
  /** When `predecessorUniqueId` holds MS Project Task ID (misnamed), `task.id` matches but `task.uniqueId` does not. */
  const stepMspFromMisnamedUidRaw = findTaskIndexByMspTaskId(tasks, link.predecessorUniqueId);
  const stepWbsRaw = findTaskIndexByWbs(tasks, link.predecessorWbs);
  const stepNameExclRaw =
    forRowIndex != null
      ? findTaskIndexByExactNameExcluding(tasks, link.predecessorName, forRowIndex)
      : null;
  const stepNameRaw = findTaskIndexByExactName(tasks, link.predecessorName);

  const accept = (idx: number | null): number | null => {
    if (idx == null) return null;
    if (forRowIndex != null && idx === forRowIndex) return null;
    return idx;
  };

  let idx = accept(stepMspRaw);
  if (idx != null) return idx;
  idx = accept(stepUidRaw);
  if (idx != null) return idx;
  idx = accept(stepMspFromMisnamedUidRaw);
  if (idx != null) return idx;
  idx = accept(stepWbsRaw);
  if (idx != null) return idx;
  if (forRowIndex != null) {
    idx = accept(stepNameExclRaw);
    if (idx != null) return idx;
  }
  return accept(stepNameRaw);
}

export function resolveSuccessorTaskIndex(
  link: ParsedSuccessorLink,
  tasks: EpcTaskRef[],
  forRowIndex?: number
): number | null {
  const accept = (idx: number | null): number | null => {
    if (idx == null) return null;
    if (forRowIndex != null && idx === forRowIndex) return null;
    return idx;
  };

  let idx = accept(findTaskIndexByMspTaskId(tasks, link.successorTaskId));
  if (idx != null) return idx;
  idx = accept(findTaskIndexByUniqueId(tasks, link.successorUniqueId));
  if (idx != null) return idx;
  idx = accept(findTaskIndexByMspTaskId(tasks, link.successorUniqueId));
  if (idx != null) return idx;
  idx = accept(findTaskIndexByWbs(tasks, link.successorWbs));
  if (idx != null) return idx;
  if (forRowIndex != null) {
    idx = accept(findTaskIndexByExactNameExcluding(tasks, link.successorName, forRowIndex));
    if (idx != null) return idx;
  }
  return accept(findTaskIndexByExactName(tasks, link.successorName));
}

/**
 * Driver label for UI. When `forRowIndex` is set, omits `predecessorName` if it matches the current row’s
 * name (bad exports often repeated the task name) and skips id→name resolution on the current row.
 */
export function predecessorDisplayLabel(
  link: ParsedPredecessorLink,
  tasks: EpcTaskRef[],
  forRowIndex?: number
): string {
  const cur = forRowIndex != null ? tasks[forRowIndex] : undefined;
  const linkName =
    typeof link.predecessorName === 'string' && link.predecessorName.trim()
      ? link.predecessorName.trim()
      : '';
  const curName = cur && typeof cur.name === 'string' ? cur.name.trim().toLowerCase() : '';
  const skipLinkName =
    forRowIndex != null && linkName && curName && linkName.toLowerCase() === curName;

  const nameFromMisnamedUid =
    link.predecessorTaskId == null || link.predecessorTaskId === ''
      ? resolveNameByMspTaskId(link.predecessorUniqueId, tasks, forRowIndex)
      : undefined;

  return (
    (!skipLinkName && linkName) ||
    resolveNameByMspTaskId(link.predecessorTaskId, tasks, forRowIndex) ||
    resolveNameByUniqueId(link.predecessorUniqueId, tasks, forRowIndex) ||
    nameFromMisnamedUid ||
    (link.predecessorTaskId != null && link.predecessorTaskId !== ''
      ? `Task ID ${link.predecessorTaskId}`
      : '') ||
    (link.predecessorUniqueId != null && link.predecessorUniqueId !== ''
      ? `MPXJ uid ${link.predecessorUniqueId}`
      : '') ||
    'Task'
  );
}

export function successorDisplayLabel(link: ParsedSuccessorLink, tasks: EpcTaskRef[], forRowIndex?: number): string {
  const cur = forRowIndex != null ? tasks[forRowIndex] : undefined;
  const linkName =
    typeof link.successorName === 'string' && link.successorName.trim() ? link.successorName.trim() : '';
  const curName = cur && typeof cur.name === 'string' ? cur.name.trim().toLowerCase() : '';
  const skipLinkName =
    forRowIndex != null && linkName && curName && linkName.toLowerCase() === curName;

  const succNameFromMisnamedUid =
    link.successorTaskId == null || link.successorTaskId === ''
      ? resolveNameByMspTaskId(link.successorUniqueId, tasks, forRowIndex)
      : undefined;

  return (
    (!skipLinkName && linkName) ||
    resolveNameByMspTaskId(link.successorTaskId, tasks, forRowIndex) ||
    resolveNameByUniqueId(link.successorUniqueId, tasks, forRowIndex) ||
    succNameFromMisnamedUid ||
    (link.successorTaskId != null && link.successorTaskId !== '' ? `Task ID ${link.successorTaskId}` : '') ||
    (link.successorUniqueId != null && link.successorUniqueId !== ''
      ? `MPXJ uid ${link.successorUniqueId}`
      : '') ||
    'Task'
  );
}

export function scheduleLinkStats(tasks: EpcTaskRef[]): {
  withPredecessors: number;
  withSuccessors: number;
  totalPredLinks: number;
  totalSuccLinks: number;
} {
  let withPredecessors = 0;
  let withSuccessors = 0;
  let totalPredLinks = 0;
  let totalSuccLinks = 0;
  for (const t of tasks) {
    const p = parsePredecessorLinks(t.predecessors);
    const s = parseSuccessorLinks(t.successors);
    if (p.length) withPredecessors += 1;
    if (s.length) withSuccessors += 1;
    totalPredLinks += p.length;
    totalSuccLinks += s.length;
  }
  return { withPredecessors, withSuccessors, totalPredLinks, totalSuccLinks };
}

/** Counts dependency rows by link code (pred + succ lists). */
export function countLinksByType(tasks: EpcTaskRef[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const t of tasks) {
    for (const p of parsePredecessorLinks(t.predecessors)) {
      const c = p.linkTypeCode || '?';
      counts[c] = (counts[c] ?? 0) + 1;
    }
    for (const s of parseSuccessorLinks(t.successors)) {
      const c = s.linkTypeCode || '?';
      counts[c] = (counts[c] ?? 0) + 1;
    }
  }
  return counts;
}
